import React, { useState, useEffect, useMemo } from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { FiSearch, FiX } from "react-icons/fi";
import AuthDialog from "../../../components/common/AuthDialog";
import ProfileCard from "../../common/ProfileCard/ProfileCard";
import { useNavigate, useLocation } from "react-router-dom";
import {
  logoutUserAction,
  fetchUserDataAction,
} from "../../../store/slices/authSlice";
import { isTokenExpired } from "../../../utils/authUtils";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#fff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  padding: "16px 24px",
  position: "relative",
  width: "94.44%",
  margin: "20px auto 20px auto",
  [theme.breakpoints.down("md")]: {
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: "16px 24px",
  },
}));

const SearchField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": {
      borderColor: "#DDDDDD",
    },
    "&:hover fieldset": {
      borderColor: "#DDDDDD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#DDDDDD",
    },
    "& input": {
      // fontFamily: "Red Hat Display",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "14px",
      letterSpacing: "0",
      textTransform: "capitalize",
      "&::placeholder": {
        color: "#666666",
        // fontFamily: "Red Hat Display",
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "14px",
        letterSpacing: "0",
        textTransform: "capitalize",
        opacity: 1,
      },
    },
  },
});

const DonateButton = styled(Button)({
  backgroundColor: "#F04951",
  borderRadius: "10px",
  color: "#fff",
  padding: "12px 18px",
  // fontFamily: "Red Hat Display",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "14px",
  letterSpacing: "0%",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#d63d45",
  },
});

const LoginButton = styled(Button)({
  backgroundColor: "#1D1D1B",
  borderRadius: "8px",
  color: "#fff",
  padding: "14px 24px",
  // fontFamily: "Red Hat Display",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "14px",
  letterSpacing: "0%",
  textTransform: "capitalize",
  "&:hover": {
    backgroundColor: "#000",
  },
});

const TopHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [openLogin, setOpenLogin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();
  const { isAuthenticated, user, loading } = useSelector((state) => state.auth);
  const cmsHome = useSelector((state) => state.cms.home);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch CMS data if not already loaded
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  // Extract donate redirect section from CMS (section id: 90)
  const donateRedirectSection = useMemo(() => {
    if (!cmsHome?.data || !Array.isArray(cmsHome.data)) return null;
    return cmsHome.data.find(
      (section) => section?.id === 90 && (section?.status === 1 || section?.status === true)
    );
  }, [cmsHome]);

  const donateRedirectLink = donateRedirectSection?.link || null;
  const donateTaglineText =
    donateRedirectSection?.title || "";

  const handleLoginOpen = () => setOpenLogin(true);
  const handleLoginClose = () => setOpenLogin(false);

  // Handle donate button click
  const handleDonateClick = () => {
    // If there's a link from CMS, navigate to it
    if (donateRedirectLink) {
      // Extract path from full URL if needed
      let path = donateRedirectLink;
      try {
        const url = new URL(donateRedirectLink);
        // If it's from the same origin or truehopefoundation domain, use the pathname
        if (url.origin === window.location.origin || url.hostname.includes('truehopefoundation')) {
          path = url.pathname;
        } else {
          // External URL - use window.location for same-tab navigation
          window.location.href = donateRedirectLink;
          return;
        }
      } catch (e) {
        // If it's not a valid URL, treat it as a path
        path = donateRedirectLink.startsWith('/') ? donateRedirectLink : `/${donateRedirectLink}`;
      }
      navigate(path);
    } else {
      // Fallback to default donation page
      navigate("/campaigns");
    }
  };

  // Handle search input change - navigate to search results as user types
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Navigate to search results page if there's a query (preserve spaces in the middle)
    if (value.trim()) {
      // Use the full value (not trimmed) to preserve spaces in the middle of the search term
      navigate(`/search?q=${encodeURIComponent(value)}`, { replace: true });
    } else {
      // If search is cleared, navigate to search page with no query or go to home
      if (location.pathname === "/search") {
        // Navigate to search page with empty query to show latest campaigns
        navigate("/search", { replace: true });
      }
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    if (location.pathname === "/search") {
      // Navigate to search page with no query to show latest campaigns
      navigate("/search", { replace: true });
    }
  };

  // Sync search query from URL when component mounts or location changes
  useEffect(() => {
    if (location.pathname === "/search") {
      const urlParams = new URLSearchParams(location.search);
      const queryParam = urlParams.get("q");
      // Set search query from URL, or empty string if no query param
      setSearchQuery(queryParam || "");
    } else {
      setSearchQuery("");
    }
  }, [location]);
  // Check for existing authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token && !user) {
      if (isTokenExpired(token)) {
        // Token is invalid/expired; clear and skip fetching user
        localStorage.removeItem("authToken");
        return;
      }
      dispatch(fetchUserDataAction());
    }
  }, [dispatch, user]);

  return (
    <StyledAppBar sx={{ backgroundColor: "#D9D9D933", borderRadius: "12px" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {/* Left side: Tagline and Donate button */}
        {!isMobile && (
          <Box display="flex" alignItems="center" gap={2}>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "14px",
                letterSpacing: "0%",
                textTransform: "capitalize",
                color: "#141414",
                whiteSpace: "nowrap",
              }}
            >
              {donateTaglineText}
            </Typography>
            <DonateButton onClick={handleDonateClick} aria-label="donate now">Donate Now</DonateButton>
          </Box>
        )}

        {/* Right side: Search field and Login button */}
        {isMobile ? (
          <SearchField
            fullWidth
            size="small"
            placeholder="Search By Campaign Title Or NGO Name"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              width: "100%",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              top: "0",
              background: "transparent",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ marginRight: "10px" }}>
                  <FiSearch size={18} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ padding: "4px" }}
                  >
                    <FiX size={18} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Box display="flex" alignItems="center" gap={2}>
            <SearchField
              size="small"
              placeholder="Search By Campaign Title Or NGO Name"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                minWidth: "318px",
                maxWidth: "400px",
                background: "#FFFFFF",
                borderRadius: "10px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ marginRight: "10px" }}>
                    <FiSearch size={18} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ padding: "4px", marginRight: "4px" }}
                    >
                      <FiX size={18} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {isAuthenticated && user ? (
              <ProfileCard user={user} />
            ) : (
              <LoginButton
                aria-label="login or signup"
                onClick={handleLoginOpen}
              >
                Login/Signup
              </LoginButton>
            )}
          </Box>
        )}
      </Box>
      <Dialog
        open={openLogin}
        onClose={handleLoginClose}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "1066px",
            width: "100%",
            borderRadius: "22px",
            overflow: "visible",
          },
        }}
      >
        <Box position="absolute" top={8} right={8} zIndex={1}>
          <IconButton onClick={handleLoginClose} size="small">
            {/* Optionally add <CloseIcon /> here */}
          </IconButton>
        </Box>
        <AuthDialog
          open={openLogin}
          onClose={() => setOpenLogin(false)}
          initialView="login"
        />
      </Dialog>
    </StyledAppBar>
  );
};

export default TopHeader;
